import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactPlayer from 'react-player';
import styles from './Experimental.module.css';

const AMBIENT_VIDEO_URLS = [
  'https://youtu.be/2BZKEK5Gi2w',
  'https://youtu.be/UPsB9aV8NXE',
  'https://youtu.be/DdTsRV7KKmM',
  'https://youtu.be/kFezEHgXfR4',
  'https://youtu.be/jl-dCcH1qJI',
  'https://youtu.be/vcNa-_DsC38',
  'https://youtu.be/R9qhsp_fJMM',
  'https://youtu.be/Mt1Mn1FmJWg',
  'https://youtu.be/ez0yF9zrXFo',
];

function youtubeVideoId(url) {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  try {
    const u = new URL(trimmed);
    if (u.hostname === 'youtu.be') {
      return u.pathname.replace(/^\//, '').split('/')[0].split('?')[0];
    }
    if (u.hostname.includes('youtube.com')) {
      const v = u.searchParams.get('v');
      if (v) return v;
      const embed = u.pathname.match(/\/embed\/([^/?]+)/);
      if (embed) return embed[1];
    }
  } catch {
    /* ignore */
  }
  const m = trimmed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : '';
}

/**
 * Minimal playerVars — heavy / duplicate flags (rel, forced embed URL, origin, loop+playlist)
 * often trigger "Video unavailable" with react-player + multiple iframes.
 * Loop is handled by the ReactPlayer `loop` prop only.
 */
function youtubePlayerVars() {
  return {
    autoplay: 1,
    controls: 0,
    modestbranding: 1,
    playsinline: 1,
    iv_load_policy: 3,
    cc_load_policy: 0,
    disablekb: 1,
    fs: 1,
    origin: window.location.origin,
  };
}

function useCoarsePointer() {
  const [coarse, setCoarse] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(pointer: coarse)');
    const apply = () => setCoarse(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);
  return coarse;
}

const TARGET_FILL = 0.65;
const HOVER_SCALE = 1.15;
const LOCK_VOLUME = 0.8;
const HOVER_VOLUME = 0.18;
const LOCK_FADE_MS = 1400;
const HOVER_FADE_MS = 600;

function AmbientYoutubeFrame({
  videoUrl,
  coarsePointer,
  shouldLoad = false,
  loopAt = 0,
  isPeerDimmed = false,
  isHoverDimmed = false,
  isHovered = false,
  isLocked = false,
  onTilePointerEnter,
  onTileClick,
  onPlaying,
}) {
  const [volume, setVolume] = useState(0);
  const [playing, setPlaying] = useState(false);
  const videoId = youtubeVideoId(videoUrl);
  const frameRef = useRef(null);
  const volRafRef = useRef(null);
  const playerRef = useRef(null);
  const durationRef = useRef(0);
  const didSeekRef = useRef(false);

  // Locked = centered fullscreen. Hovered = scale inward toward grid center.
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;

    if (isLocked) {
      el.style.transformOrigin = 'center center';
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const targetScale = Math.min(
        (vw * TARGET_FILL) / rect.width,
        (vh * TARGET_FILL) / rect.height
      );
      const dx = vw / 2 - (rect.left + rect.width / 2);
      const dy = vh / 2 - (rect.top + rect.height / 2);
      el.style.setProperty('--tx', `${dx}px`);
      el.style.setProperty('--ty', `${dy}px`);
      el.style.setProperty('--sc', targetScale);
    } else if (isHovered) {
      // Scale origin is the corner/edge facing away from viewport center
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const relX = (rect.left + rect.width / 2 - vw / 2) / (vw / 2);
      const relY = (rect.top + rect.height / 2 - vh / 2) / (vh / 2);
      const threshold = 0.2;
      const ox = relX < -threshold ? 'left' : relX > threshold ? 'right' : 'center';
      const oy = relY < -threshold ? 'top' : relY > threshold ? 'bottom' : 'center';
      el.style.transformOrigin = `${ox} ${oy}`;
      el.style.removeProperty('--tx');
      el.style.removeProperty('--ty');
      el.style.setProperty('--sc', HOVER_SCALE);
    } else {
      el.style.transformOrigin = '';
      el.style.removeProperty('--tx');
      el.style.removeProperty('--ty');
      el.style.removeProperty('--sc');
    }
  }, [isHovered, isLocked]);

  // Volume: fade to target level based on state; instant silence on leave
  useEffect(() => {
    cancelAnimationFrame(volRafRef.current);
    const targetVol = isLocked ? LOCK_VOLUME : isHovered ? HOVER_VOLUME : 0;
    const fadeDuration = isLocked ? LOCK_FADE_MS : HOVER_FADE_MS;
    if (targetVol === 0) {
      setVolume(0);
      return;
    }
    const t0 = performance.now();
    const fade = (now) => {
      const t = Math.min(1, (now - t0) / fadeDuration);
      setVolume(targetVol * t);
      if (t < 1) volRafRef.current = requestAnimationFrame(fade);
    };
    volRafRef.current = requestAnimationFrame(fade);
    return () => cancelAnimationFrame(volRafRef.current);
  }, [isHovered, isLocked]);

  const frameHandlers = coarsePointer
    ? {
        onClick: () => {
          // Unmute synchronously within the gesture — iOS requires this
          const internal = playerRef.current?.getInternalPlayer();
          internal?.unMute?.();
          internal?.setVolume?.(Math.round(LOCK_VOLUME * 100));
          onTileClick();
          // Enter fullscreen on the iframe; unlock orientation so landscape works
          const iframe = playerRef.current?.wrapper?.querySelector('iframe');
          const el = iframe ?? frameRef.current;
          const fsRequest = el?.requestFullscreen ?? el?.webkitRequestFullscreen;
          if (fsRequest) {
            fsRequest.call(el).then(() => {
              screen.orientation?.unlock?.();
            }).catch(() => {});
          }
        },
      }
    : {
        onPointerEnter: onTilePointerEnter,
        onClick: onTileClick,
      };

  if (!videoId) return null;

  return (
    <div
      ref={frameRef}
      className={styles.frame}
      data-ambient-url={videoUrl}
      data-dimmed={isPeerDimmed ? 'true' : undefined}
      data-hover-dimmed={isHoverDimmed ? 'true' : undefined}
      data-hover={isHovered ? 'true' : undefined}
      data-focus={isLocked ? 'true' : undefined}
    >
      <div className={styles.playerShell}>
        <div className={styles.loadingCover} data-playing={playing ? 'true' : undefined} />
        {shouldLoad && <ReactPlayer
          ref={playerRef}
          url={videoUrl}
          className={styles.reactPlayer}
          width="100%"
          height="100%"
          playing
          muted={volume < 0.01}
          controls={false}
          loop={loopAt === 0}
          playsinline
          volume={volume}
          onProgress={({ playedSeconds }) => {
            if (loopAt <= 0) return;
            if (playedSeconds >= loopAt && !didSeekRef.current) {
              didSeekRef.current = true;
              playerRef.current?.seekTo(0, 'seconds');
            } else if (playedSeconds < 2) {
              didSeekRef.current = false;
            }
          }}
          onPlay={() => { setPlaying(true); onPlaying?.(); }}
          onPause={() => {
            playerRef.current?.getInternalPlayer()?.playVideo?.();
          }}
          config={{
            youtube: {
              playerVars: youtubePlayerVars(),
            },
          }}
        />}
      </div>
<div className={styles.hitLayer} role="presentation" {...frameHandlers} />
    </div>
  );
}

function Experimental({ videoUrls = AMBIENT_VIDEO_URLS }) {
  const coarsePointer = useCoarsePointer();
  const [loadedCount, setLoadedCount] = useState(1);
  const [hoverFocusUrl, setHoverFocusUrl] = useState(null);
  const [clickLockedUrl, setClickLockedUrl] = useState(null);
  const suppressNextTileClickRef = useRef(false);
  const suppressHoverUntilMoveRef = useRef(false);
  const urls = Array.isArray(videoUrls) ? videoUrls : AMBIENT_VIDEO_URLS;
  const playable = urls.filter((u) => youtubeVideoId(u));

  const [mouseDwelling, setMouseDwelling] = useState(true);
  const dwellTimerRef = useRef(null);

  const clearHoverFocus = useCallback(() => setHoverFocusUrl(null), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setClickLockedUrl(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      const isFs = !!(document.fullscreenElement ?? document.webkitFullscreenElement);
      if (!isFs) setClickLockedUrl(null);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  const activeFocusUrl = coarsePointer
    ? clickLockedUrl
    : clickLockedUrl ?? hoverFocusUrl;

  const handlePagePointerDownCapture = useCallback(
    (e) => {
      if (!clickLockedUrl || e.button !== 0) return;
      const hit = e.target.closest?.('[data-ambient-url]');
      const hitUrl = hit?.getAttribute?.('data-ambient-url');
      if (hitUrl === clickLockedUrl) return;
      setClickLockedUrl(null);
      setHoverFocusUrl(null);
      suppressNextTileClickRef.current = true;
      suppressHoverUntilMoveRef.current = true;
    },
    [clickLockedUrl]
  );

  const handleTileClick = useCallback((url) => {
    if (suppressNextTileClickRef.current) {
      suppressNextTileClickRef.current = false;
      return;
    }
    suppressHoverUntilMoveRef.current = false;
    setClickLockedUrl(url);
    if (!coarsePointer) setHoverFocusUrl(url);
  }, [coarsePointer]);

  if (playable.length === 0) return null;

  return (
    <div>
    <p className={styles.soundHint}>{coarsePointer ? 'tap for sound' : 'click for sound'}</p>
    <div
      className={styles.page}
      onPointerLeave={coarsePointer ? undefined : clearHoverFocus}
      onPointerMove={
        coarsePointer
          ? undefined
          : () => {
              if (suppressHoverUntilMoveRef.current) {
                suppressHoverUntilMoveRef.current = false;
              }
              setMouseDwelling(false);
              clearTimeout(dwellTimerRef.current);
              dwellTimerRef.current = setTimeout(() => setMouseDwelling(true), 250);
            }
      }
      onPointerDownCapture={
        clickLockedUrl ? handlePagePointerDownCapture : undefined
      }
    >
      <div
        className={styles.backdrop}
        data-active={clickLockedUrl !== null ? 'true' : undefined}
        data-hover-active={mouseDwelling && !coarsePointer && hoverFocusUrl !== null && clickLockedUrl === null ? 'true' : undefined}
      />
      {playable.map((url, i) => (
        <AmbientYoutubeFrame
          key={url}
          videoUrl={url}
          coarsePointer={coarsePointer}
          shouldLoad={i < loadedCount}
          loopAt={i === 2 ? 35 : 0}
          isLocked={clickLockedUrl === url}
          isHovered={!coarsePointer && hoverFocusUrl === url && clickLockedUrl === null}
          isPeerDimmed={clickLockedUrl !== null && clickLockedUrl !== url}
          isHoverDimmed={mouseDwelling && !coarsePointer && hoverFocusUrl !== null && hoverFocusUrl !== url && clickLockedUrl === null}
          onTilePointerEnter={
            coarsePointer
              ? undefined
              : () => {
                  if (!suppressHoverUntilMoveRef.current) {
                    setHoverFocusUrl(url);
                  }
                }
          }
          onTileClick={() => handleTileClick(url)}
          onPlaying={() => {
            const delay = i === 0 ? 2500 : 0;
            setTimeout(() => setLoadedCount(c => Math.max(c, i + 2)), delay);
          }}
        />
      ))}
    </div>
    </div>
  );
}

export default Experimental;
